import {
  Box,
  Heading,
  VStack,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";

import { OfferFullDetails } from "../../Platform/Common/types";
import {
  getThemeColors,
  getOfferTierStyles,
  offerMarkdownComponents,
} from "./OfferCardShared";

interface FreeOfferCardProps {
  offer: OfferFullDetails;
  domainSlug: string;
}

const FreeOfferCard: React.FC<FreeOfferCardProps> = ({
  offer,
  domainSlug,
}) => {
  const navigate = useNavigate();

  const colors = getThemeColors(offer.theme_key);
  const badgeColors = getThemeColors(offer.badge_theme);

  const tierStyles = getOfferTierStyles(
    offer.promotion_tier,
    colors
  );

  const cardBg = useColorModeValue(
    tierStyles.cardBgLight,
    tierStyles.cardBgDark
  );

  const borderColor = useColorModeValue(
    tierStyles.borderColorLight,
    tierStyles.borderColorDark
  );

  const topBarBg = useColorModeValue(
    tierStyles.topBarBgLight,
    tierStyles.topBarBgDark
  );

  const badgeBg = useColorModeValue(
    badgeColors.badgeBgLight,
    badgeColors.badgeBgDark
  );

  const badgeText = useColorModeValue(
    badgeColors.badgeTextLight,
    badgeColors.badgeTextDark
  );

  const durationBg = useColorModeValue(
    "gray.100",
    "gray.700"
  );

  const descColor = useColorModeValue(
    "gray.600",
    "gray.300"
  );

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      borderWidth={tierStyles.borderWidth}
      borderColor={borderColor}
      boxShadow={tierStyles.boxShadow}
      position="relative"
      overflow="visible"
      transition="all 0.25s ease"
      h="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      cursor="pointer"
      _hover={{
        transform: tierStyles.hoverTransform,
        boxShadow: tierStyles.hoverShadow,
      }}
      onClick={() =>
        navigate(
          `/practice/${domainSlug}/${offer.slug}`
        )
      }
    >
      {/* Tier accent bar */}
      <Box
        position="absolute"
        top="-1px"
        left="-1px"
        right="-1px"
        h={
          offer.promotion_tier === "HERO"
            ? "7px"
            : offer.promotion_tier === "FEATURED"
              ? "5px"
              : "3px"
        }
        bg={topBarBg}
        borderTopRadius="xl"
      />

      {offer.badge_text && (
        <Box
          position="absolute"
          top={
            offer.promotion_tier === "HERO"
              ? "-16px"
              : "-13px"
          }
          left={
            offer.promotion_tier === "HERO"
              ? "22px"
              : "20px"
          }
          bg={badgeBg}
          color={badgeText}
          px={
            offer.promotion_tier === "HERO"
              ? 4
              : 3
          }
          py={
            offer.promotion_tier === "HERO"
              ? 1.5
              : 1
          }
          fontSize={
            offer.promotion_tier === "HERO"
              ? "sm"
              : "xs"
          }
          borderRadius="full"
          fontWeight="700"
          letterSpacing="0.01em"
          boxShadow="sm"
          zIndex={2}
        >
          {offer.badge_text}
        </Box>
      )}

      <VStack
        align="start"
        spacing={
          offer.promotion_tier === "HERO"
            ? 5
            : 4
        }
        flex="1"
        p={tierStyles.padding}
        pt={
          offer.promotion_tier === "HERO"
            ? 8
            : 7
        }
      >
        <Heading
          size={tierStyles.titleSize}
          fontWeight={tierStyles.titleWeight}
          fontFamily="heading"
          letterSpacing="-0.025em"
          lineHeight="1.2"
        >
          {offer.name}
        </Heading>

        {offer.duration_label && (
          <Box
            fontSize={
              offer.promotion_tier === "HERO"
                ? "sm"
                : "xs"
            }
            px={3}
            py={1}
            borderRadius="full"
            bg={durationBg}
            fontWeight="600"
          >
            {offer.duration_label}
          </Box>
        )}

        {offer.description && (
          <Box
            fontSize={
              offer.promotion_tier === "HERO"
                ? "md"
                : "sm"
            }
            color={descColor}
            lineHeight="1.65"
          >
            <ReactMarkdown components={offerMarkdownComponents}>
              {offer.description}
            </ReactMarkdown>
          </Box>
        )}
      </VStack>

      <Box
        px={tierStyles.padding}
        pb={tierStyles.padding}
        pt={2}
      >
        <Button
          w="100%"
          size={tierStyles.buttonSize}
          fontWeight="700"
          colorScheme={offer.theme_key?.toLowerCase()}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/practice/${domainSlug}/${offer.slug}`
            );
          }}
        >
          Start Free Practice
        </Button>
      </Box>
    </Box>
  );
};

export default FreeOfferCard;